import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress, Dialog, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ShareIcon from "@material-ui/icons/Share";

// Catalogo publico da empresa: pagina sem login, como o catalogo do WhatsApp
// Business. Os dados vem de /public-catalogo/:slug (so produtos disponiveis).
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const VERDE = "#25D366";

const useStyles = makeStyles(() => ({
  page: {
    minHeight: "100vh",
    background: "#f4f5f7",
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
    color: "#111827"
  },
  topo: {
    background: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky",
    top: 0,
    zIndex: 5
  },
  topoDentro: { maxWidth: 1080, margin: "0 auto", padding: "16px 16px 12px" },
  linhaTitulo: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  empresa: { fontSize: 22, fontWeight: 700, margin: 0, lineHeight: 1.2 },
  contagem: { fontSize: 13, color: "#6b7280", marginTop: 2 },
  busca: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    background: "#f3f4f6",
    borderRadius: 10,
    padding: "8px 12px",
    "& input": { border: 0, outline: "none", background: "transparent", fontSize: 15, flex: 1, minWidth: 0 }
  },
  chips: { display: "flex", gap: 8, overflowX: "auto", marginTop: 12, paddingBottom: 2, scrollbarWidth: "none" },
  chip: {
    flex: "0 0 auto",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    borderRadius: 999,
    padding: "6px 14px",
    fontSize: 13,
    cursor: "pointer",
    color: "#374151"
  },
  chipAtivo: { background: "#111827", borderColor: "#111827", color: "#ffffff" },
  grade: {
    maxWidth: 1080,
    margin: "0 auto",
    padding: 16,
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))"
  },
  card: {
    background: "#ffffff",
    borderRadius: 12,
    overflow: "hidden",
    cursor: "pointer",
    border: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
    transition: "box-shadow .15s",
    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,.08)" }
  },
  foto: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", background: "#e5e7eb", display: "block" },
  semFoto: {
    width: "100%",
    aspectRatio: "1 / 1",
    background: "#e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: 13
  },
  cardTexto: { padding: "10px 12px 12px" },
  nome: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.3,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden"
  },
  preco: { fontSize: 15, fontWeight: 700, marginTop: 6 },
  vazio: { textAlign: "center", color: "#6b7280", padding: "60px 16px" },
  centro: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 8, color: "#6b7280", padding: 16, textAlign: "center" },
  detalhe: { background: "#ffffff", maxWidth: 560, width: "100%" },
  galeria: { position: "relative", background: "#111827" },
  galeriaFoto: { width: "100%", maxHeight: "60vh", objectFit: "contain", display: "block", margin: "0 auto" },
  seta: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(255,255,255,.85) !important"
  },
  fechar: { position: "absolute", top: 8, right: 8, background: "rgba(255,255,255,.85) !important", zIndex: 2 },
  miniaturas: { display: "flex", gap: 6, overflowX: "auto", padding: "8px 12px 0" },
  miniatura: { width: 52, height: 52, objectFit: "cover", borderRadius: 6, cursor: "pointer", opacity: 0.55, flex: "0 0 auto" },
  miniaturaAtiva: { opacity: 1, outline: "2px solid #111827" },
  detalheTexto: { padding: 16 },
  detalheNome: { fontSize: 19, fontWeight: 700, margin: 0 },
  detalhePreco: { fontSize: 20, fontWeight: 700, marginTop: 6 },
  descricao: { fontSize: 14, color: "#374151", whiteSpace: "pre-line", marginTop: 12, lineHeight: 1.5 },
  botoes: { display: "flex", flexDirection: "column", gap: 8, padding: "0 16px 16px" },
  botao: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 10,
    padding: "12px 16px",
    fontSize: 15,
    fontWeight: 600,
    textDecoration: "none",
    border: 0,
    cursor: "pointer"
  },
  botaoZap: { background: VERDE, color: "#ffffff" },
  botaoComprar: { background: "#111827", color: "#ffffff" },
  compartilhar: { color: "#374151" }
}));

const formatarPreco = v =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? ""
    : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const semAcento = t =>
  String(t || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const linkWhatsapp = (numero, texto) => `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;

const CatalogoPublico = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const [dados, setDados] = useState(null);
  const [estado, setEstado] = useState("carregando");
  const [busca, setBusca] = useState("");
  const [categoria, setCategoria] = useState(null);
  const [aberto, setAberto] = useState(null);
  const [fotoAtual, setFotoAtual] = useState(0);

  useEffect(() => {
    axios
      .get(`${BACKEND}/public-catalogo/${encodeURIComponent(slug)}`)
      .then(r => {
        setDados(r.data);
        setEstado("ok");
        document.title = `${r.data.empresa} — Catálogo`;
      })
      .catch(() => setEstado("erro"));
  }, [slug]);

  const visiveis = useMemo(() => {
    if (!dados) return [];
    const termo = semAcento(busca).trim();
    return dados.produtos.filter(
      p =>
        (!categoria || p.categoriaId === categoria) &&
        (!termo || semAcento(`${p.nome} ${p.descricao}`).includes(termo))
    );
  }, [dados, busca, categoria]);

  const abrir = p => {
    setAberto(p);
    setFotoAtual(0);
  };

  const compartilhar = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: dados.empresa, url });
      else {
        await navigator.clipboard.writeText(url);
        alert("Link copiado!");
      }
    } catch (e) {
      /* a pessoa cancelou */
    }
  };

  if (estado === "carregando") {
    return (
      <div className={classes.centro}>
        <CircularProgress size={32} />
      </div>
    );
  }
  if (estado === "erro") {
    return (
      <div className={classes.centro}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#111827" }}>Catálogo não encontrado</div>
        <div>O link pode estar errado ou o catálogo foi desligado pela loja.</div>
      </div>
    );
  }

  const fotos = aberto ? aberto.fotos : [];

  return (
    <div className={classes.page}>
      <div className={classes.topo}>
        <div className={classes.topoDentro}>
          <div className={classes.linhaTitulo}>
            <div>
              <h1 className={classes.empresa}>{dados.empresa}</h1>
              <div className={classes.contagem}>
                {dados.produtos.length} {dados.produtos.length === 1 ? "produto" : "produtos"}
              </div>
            </div>
            <IconButton className={classes.compartilhar} onClick={compartilhar} aria-label="Compartilhar catálogo">
              <ShareIcon />
            </IconButton>
          </div>
          <label className={classes.busca}>
            <SearchIcon style={{ color: "#9ca3af", fontSize: 20 }} />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar produto" />
          </label>
          {dados.categorias.length > 0 && (
            <div className={classes.chips}>
              <button
                type="button"
                className={`${classes.chip} ${!categoria ? classes.chipAtivo : ""}`}
                onClick={() => setCategoria(null)}
              >
                Todos
              </button>
              {dados.categorias.map(c => (
                <button
                  type="button"
                  key={c.id}
                  className={`${classes.chip} ${categoria === c.id ? classes.chipAtivo : ""}`}
                  onClick={() => setCategoria(categoria === c.id ? null : c.id)}
                >
                  {c.nome}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {visiveis.length === 0 ? (
        <div className={classes.vazio}>
          {dados.produtos.length === 0 ? "Esta loja ainda não tem produtos no catálogo." : "Nenhum produto encontrado."}
        </div>
      ) : (
        <div className={classes.grade}>
          {visiveis.map(p => (
            <div key={p.id} className={classes.card} onClick={() => abrir(p)} role="button" tabIndex={0}
              onKeyDown={e => (e.key === "Enter" ? abrir(p) : null)}>
              {p.fotos[0] ? (
                <img className={classes.foto} src={p.fotos[0]} alt={p.nome} loading="lazy" />
              ) : (
                <div className={classes.semFoto}>sem foto</div>
              )}
              <div className={classes.cardTexto}>
                <div className={classes.nome}>{p.nome}</div>
                {formatarPreco(p.valor) && <div className={classes.preco}>{formatarPreco(p.valor)}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!aberto} onClose={() => setAberto(null)} fullWidth maxWidth="sm" scroll="body">
        {aberto && (
          <div className={classes.detalhe}>
            <div className={classes.galeria}>
              <IconButton size="small" className={classes.fechar} onClick={() => setAberto(null)} aria-label="Fechar">
                <CloseIcon />
              </IconButton>
              {fotos.length > 0 ? (
                <img className={classes.galeriaFoto} src={fotos[fotoAtual]} alt={aberto.nome} />
              ) : (
                <div className={classes.semFoto}>sem foto</div>
              )}
              {fotos.length > 1 && (
                <>
                  <IconButton size="small" className={classes.seta} style={{ left: 8 }} aria-label="Foto anterior"
                    onClick={() => setFotoAtual((fotoAtual - 1 + fotos.length) % fotos.length)}>
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton size="small" className={classes.seta} style={{ right: 8 }} aria-label="Próxima foto"
                    onClick={() => setFotoAtual((fotoAtual + 1) % fotos.length)}>
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}
            </div>
            {fotos.length > 1 && (
              <div className={classes.miniaturas}>
                {fotos.map((f, i) => (
                  <img key={f} src={f} alt="" loading="lazy" onClick={() => setFotoAtual(i)}
                    className={`${classes.miniatura} ${i === fotoAtual ? classes.miniaturaAtiva : ""}`} />
                ))}
              </div>
            )}
            <div className={classes.detalheTexto}>
              <h2 className={classes.detalheNome}>{aberto.nome}</h2>
              {formatarPreco(aberto.valor) && <div className={classes.detalhePreco}>{formatarPreco(aberto.valor)}</div>}
              {aberto.descricao && <div className={classes.descricao}>{aberto.descricao}</div>}
            </div>
            <div className={classes.botoes}>
              {dados.whatsapp && (
                <a className={`${classes.botao} ${classes.botaoZap}`} target="_blank" rel="noopener noreferrer"
                  href={linkWhatsapp(dados.whatsapp, `Olá! Tenho interesse neste produto: ${aberto.nome}${formatarPreco(aberto.valor) ? ` (${formatarPreco(aberto.valor)})` : ""}`)}>
                  <WhatsAppIcon /> Pedir pelo WhatsApp
                </a>
              )}
              {/^https?:\/\//i.test(aberto.linkCompra || "") && (
                <a className={`${classes.botao} ${classes.botaoComprar}`} href={aberto.linkCompra} target="_blank" rel="noopener noreferrer">
                  Comprar agora
                </a>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default CatalogoPublico;
